<!-- PlanningPage.vue -->
<template>
  <div class="p-4 relative">
    <PlanOverlay
      :visible="planningPerm == 'none'"
      :reason="planningPerm == 'none' && isSubUser ? 'permission' : 'plan'"
    />
    <!-- <h1 v-if="!project" class="font-bold m-0 text-2xl select-none w-full text-left">Planning</h1> -->
    <div class="flex justify-between items-center mb-4 pl-[200px]">
      <button class="w-9 h-9 !m-0 flex items-center justify-center" @click="popupVisible = true" v-if="!project && planningPerm == 'modification'">
        <i class="fas fa-cog"></i>
      </button>
      <div v-else></div>

      <!-- Sélecteur de vue -->
      <ViewSelector :views="state.views"/>

      <!-- Boutons de navigation -->
      <NavigationButtons />
    </div>

    <!-- Intégration du PlanningGrid -->
    <PlanningGrid @rows-update="handleCreateRows"/>
  </div>
  <PopupParametersPlanning :visible="popupVisible" @close="popupVisible=false"/>
</template>

<script>
import ViewSelector from '@/components/Planning/ViewSelector.vue';
import NavigationButtons from '@/components/Planning/NavigationButtons.vue';
import PlanningGrid from '@/components/Planning/PlanningGrid.vue';
import PopupParametersPlanning from '@/components/Popups/PopupParametersPlanning.vue';
import PlanOverlay from '@/components/Others/PlanOverlay.vue';

import { getWeek, getMonth, getYear, startOfWeek, startOfMonth, startOfYear } from 'date-fns';

import { fetchRessources } from '@/services/ressourceService';
import { fetchEvents, fetchEventsByProjectId, fetchEventsByGlobal } from '@/services/eventService';
import { fetchUserParameters, updateUserParameters } from '@/services/parameterService';
import { fetchClients } from '@/services/clientService';

import { reactive, provide, computed, watch, inject } from 'vue';

export default {
  components: {
    ViewSelector,
    NavigationButtons,
    PlanningGrid,
    PopupParametersPlanning,
    PlanOverlay,
  },
  props: ['project'],
  data() {
    return {
      popupVisible: false,
    };
  },
  computed: {
    planningPerm() {
      return this.perm().planning;
    },
  },
  setup() {
    const state = reactive({
      startDate: new Date(),
      endDate: new Date(),
      selectedView: {name: 'Jour', add: 1, speed: 200},
      selectedRows: {name: '', rows: []},
      views: [
        {name: 'Jour', add: 1, speed: 200, nbDays: 0}, 
        {name: 'Semaine', add: 1, speed: 100, nbDays: 7}, 
        {name: 'Mois', add: 2, speed: 50, nbDays: 32}, 
        {name: 'Trimestre', add: 3, speed: 40, nbDays: 96}, 
        {name: 'Semestre', add: 4, speed: 30, nbDays: 190}, 
        {name: 'Année', add: 10, speed: 5, nbDays: 368},
      ],
      rows: [],
      project: null,
    });

    provide('state', state);

    const days = computed(() => {
      const days = [];

      if(days.length === 0) {
        const current = new Date(new Date(state.startDate).setHours(0, 0, 0, 0));
        
        while (current <= state.endDate) {
          days.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
        return days;
      }

      // Tant que startDate est postérieure au premier jour, on le retire
      while (days.length && state.startDate > days[0]) {
        days.shift();
      }
      
      // Tant que startDate est antérieure au premier jour, on l'ajoute au début
      while (days.length && state.startDate < days[0]) {
        days.unshift(state.startDate);
      }

      // Tant que endDate est antérieure au dernier jour, on le retire
      while (days.length && state.endDate < days[days.length - 1]) {
        days.pop();
      }
      
      // Tant que endDate est postérieure au dernier jour, on l'ajoute à la fin
      while (days.length && state.endDate > days[days.length - 1]) {
        days.push(state.endDate);
      }
      
      return days;
    })

    const weeks = computed(() => {
      const weeks = [];

      let currentDay = 0;
      while (currentDay < days.value.length) {
        const week = {
          number: getWeek(days.value[currentDay], { weekStartsOn: 1 }), 
          days: 0, 
          startDate: startOfWeek(new Date(days.value[currentDay]), { weekStartsOn: 1 })
        };
        while (getWeek(days.value[currentDay], { weekStartsOn: 1 }) == week.number) {
          week.days++;
          currentDay++;
        }
        weeks.push(week);
      }
      
      return weeks;
    });

    const months = computed(() => {
      const months = [];

      let currentDay = 0;
      while (currentDay < days.value.length) {
        const month = {
          number: getMonth(days.value[currentDay]), 
          year: getYear(days.value[currentDay]), 
          days: 0, 
          startDate: startOfMonth(new Date(days.value[currentDay]))
        };
        while (getMonth(days.value[currentDay]) == month.number) {
          month.days++;
          currentDay++;
        }
        months.push(month);
      }
      
      return months;
    });

    const years = computed(() => {
      const years = [];

      let currentDay = 0;
      while (currentDay < days.value.length) {
        const year = {
          number: getYear(days.value[currentDay]), 
          days: 0, 
          startDate: startOfYear(new Date(days.value[currentDay]))
        };
        while (getYear(days.value[currentDay]) == year.number) {
          year.days++;
          currentDay++;
        }
        years.push(year);
      }
      
      return years;
    });

    const dateArrays = reactive({
      days: days,
      weeks: weeks,
      months: months,
      years: years,
    });

    provide('dateArrays', dateArrays);

    function checkForViewChange() {
      const dayDiff = Math.floor((state.endDate - state.startDate) / (1000 * 60 * 60 * 24));

      for (const view of state.views) {
        if (dayDiff >= view.nbDays) {
          state.selectedView = view;
        }
      }
    }

    provide('checkForViewChange', checkForViewChange);

    const parameters = reactive({
      startMinute: 8 * 60,
      endMinute: 18 * 60,
      daysOff: [0],
      night: false,
    });

    const loadParameters = async () => {
      const userParameters = await fetchUserParameters();
      parameters.startMinute = userParameters.startMinute;
      parameters.endMinute = userParameters.endMinute;
      parameters.daysOff = userParameters.daysOff;
      parameters.night = userParameters.night;
    };

    loadParameters();

    watch(
      () => parameters,
      async (newParameters) => {
        await updateUserParameters(newParameters);
      },
      { deep: true }
    );

    provide('parameters', parameters);

    const perm = inject('userPerm');
    const currentUser = inject('currentUser');
    const events = inject('events');

    const planningPerm = computed(() => perm().planning);

    const isSubUser = computed(() => {
      const user = currentUser && currentUser();
      return user ? !!user.mainUserId : false;
    });

    return {
      state,
      perm,
      currentUser,
      planningPerm,
      isSubUser,
      dateArrays,
      checkForViewChange,
      parameters,
      events,
    };
  },
  methods: {
    // #region Handlers
    async handleCreateRows() {
      this.state.rows = [];
      const ressources = await fetchRessources();
      let ressourcesRows = ressources.map(ressource => ({
        ...ressource,
        name: `${ressource.firstName} ${ressource.lastName}`
      }));

      if (this.perm().planning === 'consultation_solo') {
        const user = this.currentUser && this.currentUser();
        if (user) {
          ressourcesRows = ressourcesRows.filter(
            ressource => ressource.connectUserId === user.id
          );
        } else {
          ressourcesRows = [];
        }
      }

      if (this.project) {
        const usedRessourceIds = Array.from(
          new Set(
            this.events
              .flatMap(event => (event.ressources || []).map(r => r.ressourceId || r.id))
              .filter(Boolean)
          )
        );
        // Filtre les ressources globales pour ne garder que celles utilisées dans les events
        const filteredRows = ressourcesRows.filter(ressource => usedRessourceIds.includes(ressource.id));
        this.state.rows.push({
          name: 'Ressources',
          rows: filteredRows
        });
      } else {
        this.state.rows.push({
          name: 'Ressources',
          rows: ressourcesRows
        });
      }

      let uniqueTeams;
      if (this.perm().planning === 'consultation_solo') {
        const user = this.currentUser && this.currentUser();
        // Trouve la ressource liée à l'utilisateur courant
        const userRessource = ressources.find(r => r.connectUserId === user?.id && r.team);
        uniqueTeams = userRessource && userRessource.team ? [userRessource.team] : [];
      } else {
        uniqueTeams = Array.from(new Set(ressources.map(ressource => ressource.team).filter(Boolean)));
      }
      this.state.rows.push({
        name: "Equipes",
        rows: uniqueTeams.map(team => ({
          id: team,
          name: team,
          color: '#1E1017',
        }))
      });

      if(!this.project && this.perm().projects != 'none' && this.perm().planning != 'consultation_solo') {
        const clients = await fetchClients();
        this.state.rows.push({name: "Clients", rows: clients.map(client => ({
          id: client.id,
          name: client.contacts[0].lastName + ' ' + client.contacts[0].firstName,
          color: '#1E1017',
          ...client
        }))});
      }
      else if(this.perm().planning != 'consultation_solo' || this.project && this.perm().projects != 'none') {
        // Récupère tous les titres uniques des events
        const uniqueTitles = Array.from(
          new Set(this.events.map(event => event.title).filter(Boolean))
        );
        // Ajoute une row par titre
        this.state.rows.push({
          name: "Actions",
          rows: uniqueTitles.map(title => ({
            id: title,
            name: title,
            color: '#1E1017'
          }))
        });
      }

      // Sauvegarde l'ancienne sélection
      const previousSelection = this.state.selectedRows?.name;
      // Essaie de retrouver la même row dans la nouvelle liste
      const found = this.state.rows.find(row => row.name === previousSelection);
      if (found) {
        this.state.selectedRows = found;
      } else {
        // Sinon, sélectionne "Ressources" par défaut
        this.state.selectedRows = this.state.rows.find(row => row.name === "Ressources") || this.state.rows[0];
      }
    },
    // #endregion Handlers
  },
  async mounted() {
    this.state.project = this.project;

    if(this.project){
      this.events.splice(0, this.events.length, ...await fetchEventsByProjectId(this.project.id));
    }
    else{
      this.events.splice(0, this.events.length, ...await fetchEventsByGlobal());
    }
    console.log(this.events);
    this.handleCreateRows();
  },
};
</script>
